import { useEffect, useState } from 'react'
import { useClient } from '@/hooks/useClient'
import type { CustomTestPanel } from '../customPanelStorage'
import { logError } from './errorUtils'

export type SharedPanelTest = {
  code: string
  name: string
  specimen?: string
  category?: string
  categoryDisplay?: string
}

// Practice-wide lab order sets, owned by the organization rather than the
// practitioner. Read-only here: PD-1479 only requires that every practitioner
// can see and use them, so there is no create/update/delete counterpart to
// `customPanelStorage.ts`.
//
// `panels` is returned in `CustomTestPanel` shape so shared and personal panels
// render through the same `CustomPanelsSection` (shared gets `readOnly`).
// `createdAt`/`updatedAt` are not tracked for shared panels and are filled with
// the fetch time purely to satisfy that shape — nothing displays them.
//
// `testLookup` carries the display names the endpoint resolved from the catalog.
// Merge it into a form's own test map so panel rows and the order cart show real
// names instead of bare order codes — the form's catalog slice only holds
// favorites plus the current search, which usually misses a panel's tests. A code
// our catalog does not have is ABSENT from the lookup (the endpoint sends
// `name: null`), which callers already render as the raw code.
export function useSharedPanels(
  vendor: string,
  options?: { enabled?: boolean },
): {
  panels: CustomTestPanel[]
  testLookup: Map<string, SharedPanelTest>
  loading: boolean
} {
  const client = useClient()
  const [panels, setPanels] = useState<CustomTestPanel[]>([])
  const [testLookup, setTestLookup] = useState<Map<string, SharedPanelTest>>(new Map())
  const [loading, setLoading] = useState(true)

  const enabled = options?.enabled !== false

  useEffect(() => {
    if (!enabled) {
      setPanels([])
      setTestLookup(new Map())
      setLoading(false)
      return
    }

    let mounted = true
    setLoading(true)

    client
      .sharedTestPanelsGet({ vendor })
      .then((data) => {
        if (!mounted) return
        const now = new Date().toISOString()
        const fetched = data?.panels ?? []
        setPanels(
          fetched.map((p) => ({
            id: p.id,
            name: p.name,
            testCodes: p.tests.map((t) => t.code),
            vendor: p.vendor,
            createdAt: now,
            updatedAt: now,
          })),
        )
        const lookup = new Map<string, SharedPanelTest>()
        for (const p of fetched) {
          for (const t of p.tests) {
            if (!t.name) continue
            lookup.set(t.code, {
              code: t.code,
              name: t.name,
              ...(t.specimen ? { specimen: t.specimen } : {}),
              ...(t.category ? { category: t.category } : {}),
              ...(t.categoryDisplay ? { categoryDisplay: t.categoryDisplay } : {}),
            })
          }
        }
        setTestLookup(lookup)
      })
      .catch((err) => {
        if (!mounted) return
        // A shared-panel failure must never take the requisition down — the
        // practitioner can still browse the catalog and use personal panels.
        logError('Shared panels fetch', err)
        setPanels([])
        setTestLookup(new Map())
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [client, vendor, enabled])

  return { panels, testLookup, loading }
}
